import React, { useEffect, useState } from 'react';
import { message, Upload } from 'antd';
import type { UploadProps } from 'antd';
import {
  ApartmentOutlined,
  ArrowRightOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  BuildOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  DownloadOutlined,
  FileTextOutlined,
  ImportOutlined,
  InboxOutlined,
  LinkOutlined,
  NodeIndexOutlined,
  PercentageOutlined,
  SendOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ENV } from '../../../config';
import { getAuthToken } from '../../../core/services/SessionStorageService';
import { usePartnershipContext } from '../../../core/context/PartnershipContext';
import styles from './UploadInput.module.css';

// ── Input types ────────────────────────────────────────────────────────────────

type InputStatus = 'available' | 'coming_soon' | 'navigate';

interface InputType {
  key: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  status: InputStatus;
  navigateTo?: string;
}

const INPUT_TYPES: InputType[] = [
  {
    key: 'partner-data',
    name: 'Partner Data',
    description: 'Partner roster, interest classes, and beginning capital accounts for this engagement.',
    icon: <TeamOutlined />,
    status: 'available',
  },
  {
    key: 'trial-balance',
    name: 'Trial Balance',
    description: 'GAAP trial balance with account mapping to the Chart of Accounts for income allocation.',
    icon: <BarChartOutlined />,
    status: 'available',
  },
  {
    key: 'fixed-assets',
    name: 'Fixed Assets',
    description: 'Asset register for MACRS depreciation and basis adjustment calculations.',
    icon: <BuildOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'prior-year-m1',
    name: 'Prior Year M1 Adjustments',
    description: 'Book-to-tax differences from the prior year for roll-forward calculations.',
    icon: <FileTextOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'cash-flow',
    name: 'Cash Flow',
    description: 'Partnership cash flow statements for distribution and allocation calculations.',
    icon: <DollarOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'distributions',
    name: 'Distributions',
    description: 'Partner distribution history and scheduled distributions for the tax year.',
    icon: <SendOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'events',
    name: 'Events',
    description: 'Partnership events including contributions, transfers, and revaluations.',
    icon: <CalendarOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'activities',
    name: 'Activities',
    description: 'Business activity data for state apportionment and allocation purposes.',
    icon: <ThunderboltOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'external-k1s',
    name: 'External K-1s',
    description: 'K-1s received from lower-tier partnerships for income pass-through.',
    icon: <InboxOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'layers',
    name: 'Layers',
    description: '704(c) layers and built-in gain/loss tracking for contributed property.',
    icon: <ApartmentOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'ratios',
    name: 'Ratios',
    description: 'Allocation ratios and special allocation schedules for partnership items.',
    icon: <PercentageOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'outstanding-debt',
    name: 'Outstanding Debt',
    description: 'Partnership debt schedule for basis allocation and at-risk calculations.',
    icon: <BankOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'nonrecourse-debt',
    name: 'Non-Recourse Debt — Asset Mapping',
    description: 'Mapping of non-recourse debt to specific partnership assets.',
    icon: <LinkOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'recourse-debt',
    name: 'Recourse Debt Allocation',
    description: 'Partner-level recourse debt allocation for outside basis calculations.',
    icon: <AuditOutlined />,
    status: 'coming_soon',
  },
  {
    key: 'account-mapping',
    name: 'Account Mapping',
    description: 'Map trial balance accounts to income categories for calculation templates.',
    icon: <NodeIndexOutlined />,
    status: 'navigate',
    navigateTo: '/calculations/account-mapping',
  },
  {
    key: 'full-setup',
    name: 'Full Setup Import',
    description: 'Import everything at once from a single configuration Excel file.',
    icon: <ImportOutlined />,
    status: 'coming_soon',
  },
];

// ── Row upload state ───────────────────────────────────────────────────────────

type RowUploadStatus = 'idle' | 'uploading' | 'success' | 'error';

interface RowState {
  status: RowUploadStatus;
  msg?: string;
}

// Reads the real, currently-persisted row count for an already-uploaded input type, so the
// screen reflects actual DB state instead of relying on in-memory upload status that resets
// on "Upload another" or on remount after navigating away (e.g. to Map Accounts) and back.
async function fetchTrialBalanceRowCount(entityId: number | string): Promise<number> {
  const res = await fetch(`${ENV.API_URL}/api/trial-balance?entityId=${entityId}`, {
    headers: {
      Authorization: `Bearer ${getAuthToken()}`,
      tenant: ENV.TENANT_ID ?? '',
    },
  });
  if (!res.ok) throw new Error(`Failed to load trial balance state (${res.status})`);
  const data = await res.json();
  return Array.isArray(data?.rows) ? data.rows.length : 0;
}

// ── UploadInput ────────────────────────────────────────────────────────────────

export const UploadInput: React.FC = () => {
  const { selectedClient, selectedPartnership, taxYear } = usePartnershipContext();
  const clientId = selectedClient?.id;
  const entityId = selectedPartnership?.id;
  const navigate = useNavigate();

  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  // Persisted DB-truth row count for "trial-balance", independent of rowStates — refetched on
  // every mount so it survives "Upload another" and navigating away and back (BA-BUG-4).
  const [trialBalanceRowCount, setTrialBalanceRowCount] = useState<number | null>(null);

  function setRowState(key: string, state: RowState) {
    setRowStates(prev => ({ ...prev, [key]: state }));
  }

  function refreshTrialBalanceCount() {
    if (!entityId) return;
    fetchTrialBalanceRowCount(entityId)
      .then(count => setTrialBalanceRowCount(count))
      .catch(err => console.error('[CalcEngine] Failed to load trial balance row count:', err));
  }

  useEffect(() => {
    setTrialBalanceRowCount(null);
    if (!entityId) return;
    let cancelled = false;
    fetchTrialBalanceRowCount(entityId)
      .then(count => { if (!cancelled) setTrialBalanceRowCount(count); })
      .catch(err => console.error('[CalcEngine] Failed to load trial balance row count:', err));
    return () => { cancelled = true; };
  }, [entityId]);

  function getUploadAction(key: string): string {
    if (key === 'partner-data') return `${ENV.API_URL}/api/entity/import?clientId=${clientId ?? ''}`;
    if (key === 'trial-balance') return `${ENV.API_URL}/api/trial-balance/import?entityId=${entityId ?? ''}`;
    return '';
  }

  function getSuccessMsg(key: string, response: unknown): string {
    if (key === 'partner-data') {
      const r = response as { imported?: number };
      return `${r?.imported ?? 0} partners imported`;
    }
    if (key === 'trial-balance') {
      const r = response as { imported?: number };
      return `Trial balance imported — ${r?.imported ?? 0} rows`;
    }
    return 'Uploaded successfully';
  }

  function makeUploadProps(key: string): UploadProps {
    return {
      accept: '.xlsx',
      showUploadList: false,
      action: getUploadAction(key),
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        tenant: ENV.TENANT_ID ?? '',
      },
      beforeUpload: (file) => {
        if (!file.name.endsWith('.xlsx')) {
          message.error('Only .xlsx files are accepted');
          return Upload.LIST_IGNORE;
        }
        return true;
      },
      onChange: (info) => {
        if (info.file.status === 'uploading') {
          setRowState(key, { status: 'uploading' });
        } else if (info.file.status === 'done') {
          const msg = getSuccessMsg(key, info.file.response);
          message.success(msg);
          setRowState(key, { status: 'success', msg });
          if (key === 'trial-balance') refreshTrialBalanceCount();
        } else if (info.file.status === 'error') {
          const errMsg = (info.file.response as { message?: string })?.message ?? 'Upload failed';
          setRowState(key, { status: 'error', msg: errMsg });
        }
      },
    };
  }

  function renderUploadCell(it: InputType) {
    const rs = rowStates[it.key] ?? { status: 'idle' };

    if (it.status === 'navigate') {
      return (
        <button className={styles.btnNavigate} onClick={() => navigate(it.navigateTo ?? '/')}>
          Go to CoA Mapping <ArrowRightOutlined />
        </button>
      );
    }

    if (it.status === 'coming_soon') {
      return (
        <button className={styles.btnUploadDisabled} disabled>
          <UploadOutlined /> Upload File
        </button>
      );
    }

    // available
    if (rs.status === 'success') {
      return (
        <div className={styles.cellSuccess}>
          <CheckCircleOutlined />
          <span>{rs.msg}</span>
          {it.key === 'trial-balance' && (
            <button className={styles.btnSecondary} onClick={() => navigate('/calculations/account-mapping')}>
              <ArrowRightOutlined /> Map Accounts
            </button>
          )}
          <button className={styles.btnReset} onClick={() => setRowState(it.key, { status: 'idle' })}>
            Upload another
          </button>
        </div>
      );
    }

    if (rs.status === 'error') {
      return (
        <div className={styles.cellError}>
          <CloseCircleOutlined />
          <span>{rs.msg}</span>
          <button className={styles.btnReset} onClick={() => setRowState(it.key, { status: 'idle' })}>
            Retry
          </button>
        </div>
      );
    }

    const loadedCount = it.key === 'trial-balance' ? trialBalanceRowCount : null;

    return (
      <div className={styles.cellIdle}>
        {!!loadedCount && (
          <div className={styles.cellLoaded}>
            <CheckCircleOutlined />
            <span>{loadedCount} trial balance row{loadedCount === 1 ? '' : 's'} already loaded</span>
            <button className={styles.btnSecondary} onClick={() => navigate('/calculations/account-mapping')}>
              <ArrowRightOutlined /> Map Accounts
            </button>
          </div>
        )}
        <Upload {...makeUploadProps(it.key)}>
          <button className={styles.btnUpload} disabled={rs.status === 'uploading'}>
            <UploadOutlined />
            {rs.status === 'uploading' ? 'Uploading…' : loadedCount ? 'Upload another file' : 'Upload File'}
          </button>
        </Upload>
      </div>
    );
  }

  function renderStatusBadge(status: InputStatus) {
    if (status === 'available') return <span className={styles.badgeReady}>Ready</span>;
    if (status === 'navigate') return <span className={styles.badgeConfigure}>Configure →</span>;
    return <span className={styles.badgeComingSoon}>Coming Soon</span>;
  }

  return (
    <div className={styles.pageWrap}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>Upload Input</h1>
          <p className={styles.pageSub}>Upload Excel files to populate calculation inputs for this engagement.</p>
        </div>
      </div>

      {selectedPartnership && (
        <div className={styles.entityBanner}>
          Uploading for:{' '}
          <strong style={{ color: 'var(--ce-text-primary)' }}>{selectedPartnership.name}</strong>
          <span className={styles.entityMeta}>
            Entity {entityId} · {taxYear}
          </span>
        </div>
      )}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Input Type</th>
              <th>Description</th>
              <th>Upload File</th>
              <th>Template</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {INPUT_TYPES.map(it => (
              <tr key={it.key} className={styles.tableRow}>
                <td>
                  <div className={styles.cellName}>
                    <span className={styles.cellIcon}>{it.icon}</span>
                    {it.name}
                  </div>
                </td>
                <td className={styles.cellDesc}>{it.description}</td>
                <td>{renderUploadCell(it)}</td>
                <td>
                  <button className={styles.btnTemplateDisabled} disabled>
                    <DownloadOutlined /> Download Template
                  </button>
                </td>
                <td>{renderStatusBadge(it.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
